import React, { memo, useMemo, useState } from 'react';
import type { Card } from '../types/game';
import PlayingCard from './PlayingCard';

interface PlayerHandProps {
  cards: Card[];
  isInteractive?: boolean;
  onCardClick?: (card: Card) => void;
  selectedCard?: Card | null;
  disabledCards?: Card[];
  playableCards?: Card[];
  maxCards?: number; // Optional override, defaults to cards.length
  currentPhase?: string;
  isMyTurn?: boolean;
}

const PlayerHand: React.FC<PlayerHandProps> = ({
  cards,
  isInteractive = false,
  onCardClick,
  selectedCard,
  disabledCards = [],
  playableCards = [],
  maxCards, // No default - use actual card count
  currentPhase = 'waiting',
  isMyTurn = false,
}) => {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [cardPlayAnimation, setCardPlayAnimation] = useState<string | null>(null);
  
  // Use actual card count if maxCards not provided
  const effectiveMaxCards = maxCards ?? cards.length;
  
  const handleEnhancedCardClick = (card: Card) => {
    const cardProps = getCardProps(card);
    
    if (cardProps.isInvalid) {
      // Trigger shake animation for invalid card
      const cardElement = document.querySelector(`[data-card-id="${card.id}"]`) as HTMLElement;
      if (cardElement) {
        cardElement.style.animation = 'invalidCardShake 0.5s ease-in-out';
        setTimeout(() => {
          cardElement.style.animation = '';
        }, 500);
      }
      return;
    }
    
    if (cardProps.isPlayable && onCardClick) {
      // Trigger play animation
      setCardPlayAnimation(card.id);
      
      // Execute the actual card play after a short delay for visual feedback
      setTimeout(() => {
        onCardClick(card);
        setCardPlayAnimation(null);
      }, 200);
    }
  };

  const handStyle = useMemo(() => {
    const cardCount = Math.min(cards.length, effectiveMaxCards);
    const cardWidth = 2.5; // Reduced card width for smaller cards
    const maxViewportWidth = typeof window !== 'undefined' ? window.innerWidth * 0.9 : 800; // 90% of viewport
    const maxAvailableWidth = Math.min(maxViewportWidth / 16, 50); // Convert px to rem, cap at 50rem
    
    // Calculate if we need overlapping
    const fullSpreadWidth = cardCount * cardWidth;
    const needsOverlap = fullSpreadWidth > maxAvailableWidth;
    
    let overlapPercentage = 0;
    let totalWidth = fullSpreadWidth;
    
    if (needsOverlap && cardCount > 1) {
      // Smart overlapping: calculate minimum overlap needed
      // For 17 cards, allow up to 40% overlap if needed
      const maxOverlapPercent = cardCount > 15 ? 0.4 : 0.1; // Increase max overlap for large hands
      const targetWidth = maxAvailableWidth;
      const requiredOverlapWidth = fullSpreadWidth - targetWidth;
      const overlapPerCard = requiredOverlapWidth / (cardCount - 1);
      overlapPercentage = Math.min(overlapPerCard / cardWidth, maxOverlapPercent);
      
      const visibleCardWidth = cardWidth * (1 - overlapPercentage);
      totalWidth = cardCount > 1 
        ? (cardCount - 1) * visibleCardWidth + cardWidth
        : cardWidth;
    }
    
    return {
      width: `${Math.max(totalWidth, 8)}rem`, // Reduced minimum width
      maxWidth: '90vw',
    };
  }, [cards.length, effectiveMaxCards]);

  const getCardProps = (card: Card) => {
    const isPlayable = isInteractive && 
                      isMyTurn && 
                      currentPhase === 'playing' && 
                      (playableCards.length === 0 || playableCards.some(pc => pc.id === card.id));
                      
    const isInvalid = isInteractive && 
                     isMyTurn && 
                     currentPhase === 'playing' && 
                     playableCards.length > 0 && 
                     !playableCards.some(pc => pc.id === card.id);
    
    const isDisabled = disabledCards.some(disabled => disabled.id === card.id);

    return {
      suit: card.suit,
      rank: card.rank,
      isSelected: selectedCard?.id === card.id,
      isPlayable: isPlayable && !isDisabled,
      isInvalid: isInvalid || isDisabled,
      onClick: () => {
        console.log('🃏🃏🃏 PLAYERHAND: Card clicked!', {
          card: {
            id: card.id,
            rank: card.rank,
            suit: card.suit
          },
          isInteractive,
          isPlayable,
          isInvalid,
          isDisabled,
          willCall: isPlayable && !isDisabled ? 'YES - calling enhanced handler' : 'NO - not calling handler'
        });
        
        if (isDisabled) {
          console.warn('❌ PLAYERHAND: Card disabled');
          return;
        }
        
        handleEnhancedCardClick(card);
      },
      onHover: (isHovering: boolean) => {
        setHoveredCard(isHovering ? card.id : null);
      },
      size: 'small' as const, // Small cards for compact display
      className: `hand-card ${hoveredCard === card.id ? 'z-50' : ''}`,
    };
  };

  if (!cards.length) {
    return (
      <div className="flex justify-center items-center h-32 text-gray-500 text-lg animate-fade-in">
        No cards in hand
      </div>
    );
  }

  return (
    <div className="flex justify-center items-end py-2">
      <style>{`
        @keyframes playableCardPulse {
          0%, 100% { 
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.3);
          }
          50% { 
            box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.1);
          }
        }
        
        @keyframes cardPlayTrajectory {
          0% { 
            transform: translateY(-16px) scale(1.08) rotateZ(3deg);
            opacity: 1;
          }
          30% { 
            transform: translateY(-40px) scale(1.1) rotateZ(-10deg);
            opacity: 0.9;
          }
          100% { 
            transform: translateY(-120px) translateX(0px) scale(0.6) rotateZ(-25deg);
            opacity: 0;
          }
        }
        
        @keyframes invalidCardShake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-2px); }
          40% { transform: translateX(2px); }
          60% { transform: translateX(-2px); }
          80% { transform: translateX(2px); }
        }
      `}</style>
      <div 
        className="relative flex items-end hand-container"
        style={{
          ...handStyle,
          filter: isMyTurn && currentPhase === 'playing' ? 'drop-shadow(0 0 8px rgba(251, 191, 36, 0.3))' : 'none'
        }}
      >
        {cards.slice(0, effectiveMaxCards).map((card, index) => {
          const cardCount = Math.min(cards.length, effectiveMaxCards);
          const cardWidth = 2.5; // Matching our reduced card width
          const maxViewportWidth = typeof window !== 'undefined' ? window.innerWidth * 0.9 : 800;
          const maxAvailableWidth = Math.min(maxViewportWidth / 16, 50);
          
          // Calculate overlap percentage (same logic as in handStyle)
          const fullSpreadWidth = cardCount * cardWidth;
          const needsOverlap = fullSpreadWidth > maxAvailableWidth;
          
          let overlapPercentage = 0;
          if (needsOverlap && cardCount > 1) {
            const maxOverlapPercent = cardCount > 15 ? 0.4 : 0.1; // Match handStyle logic
            const targetWidth = maxAvailableWidth;
            const requiredOverlapWidth = fullSpreadWidth - targetWidth;
            const overlapPerCard = requiredOverlapWidth / (cardCount - 1);
            overlapPercentage = Math.min(overlapPerCard / cardWidth, maxOverlapPercent);
          }
          
          const visibleWidth = cardWidth * (1 - overlapPercentage);
          const rightPosition = (cards.length - 1 - index) * visibleWidth;
          
          return (
            <div
              key={card.id}
              data-card-id={card.id}
              className="absolute transition-all duration-300 ease-out"
              style={{
                right: `${rightPosition}rem`,
                zIndex: hoveredCard === card.id ? 100 : selectedCard?.id === card.id ? 99 : index + 1,
                transform: (() => {
                  const isHovered = hoveredCard === card.id;
                  const isSelected = selectedCard?.id === card.id;
                  const isPlayable = getCardProps(card).isPlayable;
                  const isAnimating = cardPlayAnimation === card.id;
                  
                  if (isAnimating) {
                    return 'translateY(-60px) scale(0.8) rotateZ(-15deg)';
                  } else if (isHovered) {
                    return 'translateY(-16px) scale(1.08) rotateZ(3deg)';
                  } else if (isSelected) {
                    return 'translateY(-12px) scale(1.05) rotateZ(1deg)';
                  } else if (isPlayable) {
                    return 'translateY(-2px) scale(1.01)';
                  } else {
                    return 'translateY(0) scale(1)';
                  }
                })(),
                filter: (() => {
                  const isHovered = hoveredCard === card.id;
                  const isSelected = selectedCard?.id === card.id;
                  const isPlayable = getCardProps(card).isPlayable;
                  const isInvalid = getCardProps(card).isInvalid;
                  const isAnimating = cardPlayAnimation === card.id;
                  
                  if (isAnimating) {
                    return 'drop-shadow(0 12px 24px rgba(251, 191, 36, 0.6)) brightness(1.2) saturate(1.2)';
                  } else if (isHovered) {
                    if (isPlayable) {
                      return 'drop-shadow(0 12px 20px rgba(34, 197, 94, 0.5)) brightness(1.15) saturate(1.1)';
                    } else if (isInvalid) {
                      return 'drop-shadow(0 8px 16px rgba(239, 68, 68, 0.4)) brightness(0.9) saturate(0.8)';
                    } else {
                      return 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.3)) brightness(1.1)';
                    }
                  } else if (isSelected) {
                    return 'drop-shadow(0 6px 16px rgba(59, 130, 246, 0.5)) brightness(1.05)';
                  } else if (isPlayable) {
                    return 'drop-shadow(0 4px 8px rgba(34, 197, 94, 0.3)) brightness(1.02)';
                  } else if (isInvalid) {
                    return 'drop-shadow(0 2px 4px rgba(239, 68, 68, 0.2)) brightness(0.85) saturate(0.6)';
                  } else {
                    return 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))';
                  }
                })(),
                animation: (() => {
                  const isPlayable = getCardProps(card).isPlayable;
                  if (isMyTurn && currentPhase === 'playing' && isPlayable) {
                    return 'playableCardPulse 2s ease-in-out infinite';
                  }
                  return 'none';
                })(),
                // Add subtle border for better card state indication
                border: (() => {
                  const cardProps = getCardProps(card);
                  
                  if (isMyTurn && currentPhase === 'playing') {
                    if (cardProps.isPlayable) {
                      return '2px solid rgba(34, 197, 94, 0.4)';
                    } else if (cardProps.isInvalid) {
                      return '2px solid rgba(239, 68, 68, 0.3)';
                    }
                  }
                  return 'none';
                })(),
                borderRadius: '8px'
              }}
            >
              <PlayingCard {...getCardProps(card)} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default memo(PlayerHand);
